import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Waves, Loader2, ArrowRight, Wallet } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  ["analyst", "Analyst"], ["field_worker", "Field Worker"], ["inspector", "Inspector"],
  ["investor", "Investor"], ["public_viewer", "Public Viewer"],
  ["farmer", "Farmer"], ["cooperative", "Cooperative"], ["market_inspector", "Market Inspector"],
  ["public_authority", "Public Authority"], ["collective_executive", "Collective Executive"], ["appraiser", "Licensed Appraiser"],
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "analyst" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register(form);
      toast.success("Wallet auto-provisioned. Welcome!");
      nav("/dashboard");
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#050A15]">
      <div className="grid place-items-center p-6 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <span className="grid place-items-center h-9 w-9 rounded-sm bg-gradient-to-br from-[#8A2BE2] via-[#00FFFF] to-[#00FF7F]">
              <Waves className="h-5 w-5 text-[#050A15]" strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-lg">Quantum<span className="qs-grad-text">Spectra</span></span>
          </Link>
          <h1 className="font-display font-bold text-2xl">Create account</h1>
          <p className="text-slate-400 text-sm mt-1 mb-6">A custodial wallet is provisioned automatically.</p>

          {err && <div data-testid="register-error" className="mb-4 text-sm text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 px-4 py-2.5">{err}</div>}

          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Full name</label>
          <input data-testid="register-name" required value={form.name} onChange={upd("name")}
            className="w-full bg-[#0A1128] border border-white/10 px-4 py-3 mb-4 outline-none focus:border-[#00FFFF] transition-colors" />
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
          <input data-testid="register-email" type="email" required value={form.email} onChange={upd("email")}
            className="w-full bg-[#0A1128] border border-white/10 px-4 py-3 mb-4 outline-none focus:border-[#00FFFF] transition-colors" />
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Password</label>
          <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={upd("password")}
            className="w-full bg-[#0A1128] border border-white/10 px-4 py-3 mb-4 outline-none focus:border-[#00FFFF] transition-colors" />
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Role</label>
          <select data-testid="register-role" value={form.role} onChange={upd("role")}
            className="w-full bg-[#0A1128] border border-white/10 px-4 py-3 mb-6 outline-none focus:border-[#00FFFF] transition-colors">
            {ROLES.map(([v, l]) => <option key={v} value={v} className="bg-[#0A1128]">{l}</option>)}
          </select>
          {!["analyst", "field_worker", "investor", "public_viewer", "farmer"].includes(form.role) && <p className="text-[11px] text-[#F59E0B] -mt-4 mb-4" data-testid="role-approval-note">This role controls funds or sensitive records — your account starts as Analyst and an administrator must approve the request.</p>}

          <button data-testid="register-submit" disabled={loading}
            className="qs-cta w-full flex items-center justify-center gap-2 bg-[#00FFFF] text-[#050A15] font-semibold py-3 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create Account <ArrowRight className="h-4 w-4" /></>}
          </button>
          <p className="text-sm text-slate-400 mt-5 text-center">Have an account? <Link to="/login" className="text-[#00FFFF] hover:underline">Sign in</Link></p>
        </form>
      </div>
      <div className="hidden lg:block relative qs-noise order-1 lg:order-2">
        <div className="absolute inset-0 grid place-items-center p-16">
          <div className="qs-grad-border p-8 max-w-sm">
            <Wallet className="h-8 w-8 text-[#00FF7F]" />
            <h2 className="font-display font-bold text-2xl mt-4">Custodial wallet, zero friction.</h2>
            <p className="text-slate-400 mt-3 leading-relaxed text-sm">No MetaMask, no seed phrases. Your keys are KMS-encrypted server-side, so you can hold certificates and carbon credits with just an email.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
